import {Card, DataStatus} from 'platform/components';
import {VStack} from 'platform/foundation';

import {
  AftersalesPaymentMethod,
  useDeleteCheckoutDepositPaymentMutation,
  useGetCheckoutByIdQuery,
  useGetCheckoutPaymentsQuery,
  usePostCheckoutDepositPaymentCreateMutation,
  usePutCheckoutDepositPaymentAllowMutation,
  usePutCheckoutDepositPaymentDisallowMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CheckoutPaymentCardV2} from './CheckoutPaymentCardV2';

interface DepositPaymentsCardV2Props extends RequiredTestIdProps {
  checkoutId: string;
  defaultPaymentType?: AftersalesPaymentMethod;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
  serviceCaseId?: string;
  serviceOrderId?: string;
}

export function DepositPaymentsCardV2(props: DepositPaymentsCardV2Props) {
  const {
    data: checkoutData,
    isLoading: isCheckoutLoading,
    isError: isCheckoutError,
  } = useGetCheckoutByIdQuery({checkoutId: props.checkoutId});

  const {
    data: paymentsData,
    isLoading: isPaymentsLoading,
    isError: isPaymentsError,
  } = useGetCheckoutPaymentsQuery({checkoutId: props.checkoutId});

  const [putCheckoutDepositPaymentAllow, {isLoading: isAllowingLoading}] =
    usePutCheckoutDepositPaymentAllowMutation();
  const [putCheckoutDepositPaymentDisallow, {isLoading: isDisallowingLoading}] =
    usePutCheckoutDepositPaymentDisallowMutation();
  const [postCheckoutDepositPaymentCreate] = usePostCheckoutDepositPaymentCreateMutation();
  const [deleteCheckoutDepositPayment] = useDeleteCheckoutDepositPaymentMutation();

  const balancePayment = paymentsData?.find((payment) => payment.discriminator === 'BALANCE');

  const handleSwitchDepositPayments = (shouldAllow: boolean) => {
    if (shouldAllow) {
      return putCheckoutDepositPaymentAllow({
        checkoutId: props.checkoutId,
        balancePaymentId: balancePayment?.checkoutPaymentId,
      })
        .unwrap()
        .catch(handleApiError);
    }

    return putCheckoutDepositPaymentDisallow({
      checkoutId: props.checkoutId,
      balancePaymentId: balancePayment?.checkoutPaymentId,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleAddAnotherDeposit = () =>
    postCheckoutDepositPaymentCreate({checkoutId: props.checkoutId}).unwrap().catch(handleApiError);

  const handleRemoveDeposit = (paymentId: string) => {
    if (depositPayments?.length === 1) {
      return putCheckoutDepositPaymentDisallow({
        checkoutId: props.checkoutId,
        balancePaymentId: balancePayment?.checkoutPaymentId,
      })
        .unwrap()
        .catch(handleApiError);
    }

    return deleteCheckoutDepositPayment({checkoutId: props.checkoutId, paymentId})
      .unwrap()
      .catch(handleApiError);
  };

  const depositPayments = paymentsData?.filter((payment) => payment.discriminator === 'DEPOSIT');

  const isBalancePaymentIssued =
    balancePayment?.paymentState === 'PENDING' || balancePayment?.paymentState === 'PAID';

  const hasSomePaidPayment =
    !!depositPayments?.length && depositPayments.some((payment) => payment.paymentState === 'PAID');

  const isLoading = isPaymentsLoading || isCheckoutLoading;

  const isError = isPaymentsError || isCheckoutError;

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Card
        control={{
          type: 'switch',
          onChange: handleSwitchDepositPayments,
          value: !!checkoutData?.isDepositAllowed,
          isDisabled:
            isAllowingLoading ||
            isDisallowingLoading ||
            hasSomePaidPayment ||
            props.isEditingDisabled ||
            isBalancePaymentIssued,
        }}
        title={i18n.t('entity.checkout.deposit')}
        variant="inlineGrey"
        isExpanded={checkoutData?.isDepositAllowed ?? false}
        isClosedByDefault
      >
        <VStack spacing={4}>
          {depositPayments?.map((payment, index) => {
            const isLastPayment = depositPayments.length - 1 === index;

            return (
              <CheckoutPaymentCardV2
                key={payment.checkoutPaymentId}
                paymentId={payment.checkoutPaymentId}
                checkoutId={props.checkoutId}
                customerId={checkoutData?.billingInformation?.customerInformation.id ?? ''}
                defaultPaymentType={props.defaultPaymentType}
                onAddAnotherDepositClick={
                  isLastPayment && !isBalancePaymentIssued ? handleAddAnotherDeposit : undefined
                }
                onRemoveDepositClick={() => handleRemoveDeposit(payment.checkoutPaymentId)}
                isBalancePaymentIssued={isBalancePaymentIssued}
                isEditingDisabled={props.isEditingDisabled}
                isCompletingDisabled={props.isCompletingDisabled}
                serviceCaseId={props.serviceCaseId}
                serviceOrderId={props.serviceOrderId}
                data-testid={suffixTestId(`depositPayment-${index}`, props)}
              />
            );
          })}
        </VStack>
      </Card>
    </DataStatus>
  );
}
